import type { ProductAttributeSearchFilterGrouped } from "@commercekit/remix";
import { CKCollapsible, CKFilter } from "@commercekit/ui";
import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import CheckBoxChecked from "../icons/CheckBoxChecked";
import CheckBoxUnChecked from "../icons/CheckBoxUnChecked";
import CheckBoxUnCheckedMobile from "../icons/CheckBoxUnCheckedMobile";
import { formatFilterValue } from "~/lib/prismic/utils";

const Desktop: React.FC<ProductAttributeSearchFilterGrouped> = ({
  name,
  options,
}) => {
  const { $pt } = usePrismicLabels();
  const [expanded, setExpanded] = useState(false);
  const valuesCount = options.length;
  const valuesForDisplay = expanded ? options : options.slice(0, 5);
  return (
    <>
      {valuesCount > 0 ? (
        <CKFilter.Group>
          <div className="filters__filter mb-lg-3">
            <div className="filters__filter-heading row mx-0 mx-lg-n3">
              <div className="col">
                <div className="heading sans-serif strong">{name}</div>
              </div>

              <div className="col-auto d-lg-none align-self-center">
                <span data-icon-after=""></span>
              </div>
            </div>

            <ul className="filters__filter-options px-3 px-lg-0">
              {valuesForDisplay &&
                valuesForDisplay.map((item) => (
                  <CKFilter.Item
                    value={item}
                    key={`${name}_${item.value}`}
                    className="mt-3 mt-lg-1"
                  >
                    {({ selected, onUpdate }) => (
                      <label className="input input--checkbox filters__option">
                        <div className="input__group d-flex no-gutters">
                          <input
                            type="checkbox"
                            name={item.value}
                            defaultChecked={selected}
                            className="input__field checked"
                            onChange={(checked) => {
                              onUpdate({
                                type: "checkbox",
                                origin: [item],
                                checked: checked.target.checked,
                              });
                            }}
                          />
                          <div className="col-auto" data-icon-before="">
                            {selected ? (
                              <CheckBoxChecked />
                            ) : (
                              <CheckBoxUnChecked />
                            )}
                          </div>
                          <div className="col d-flex align-items-center pl-2">
                            <span className="input__checkbox-label text light">
                              {formatFilterValue(item.value, $pt)}
                              {item.count > 0 && (
                                <span className="ml-1 text-muted">
                                  ({item.count})
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </label>
                    )}
                  </CKFilter.Item>
                ))}
            </ul>
            {valuesCount > 5 ? (
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="filters__filter-options-more link d-none d-lg-block mt-1"
              >
                {expanded ? (
                  <span className="mr-1" data-icon-after="">
                    {$pt("filter_show_less")}
                  </span>
                ) : (
                  <span className="mr-1" data-icon-after="">
                    {$pt("filter_show_more")}
                  </span>
                )}
              </button>
            ) : null}
          </div>
        </CKFilter.Group>
      ) : null}
    </>
  );
};

const Mobile: React.FC<ProductAttributeSearchFilterGrouped> = ({
  name,
  options,
}) => {
  const { $pt } = usePrismicLabels();
  const toggleSelectedFilters = (open: boolean, id: string) => {
    let d = document.getElementById(`expandCollapse${id}`);
    if (d) {
      open && d.hasAttribute("data-open")
        ? d.removeAttribute("data-open")
        : d.setAttribute("data-open", "");
    }
  };
  return (
    <CKCollapsible
      defaultOpen={false}
      as="div"
      className="filters__filter mb-lg-3"
      id={`expandCollapse${name}`}
    >
      {({ open }) => (
        <>
          <CKCollapsible.Button
            as="div"
            className="filters__filter-heading row mx-0 mx-lg-n3"
            onClick={() => toggleSelectedFilters(open, name)}
          >
            <div className="col">
              <div className="heading sans-serif strong">{name}</div>
            </div>

            <div className="col-auto d-lg-none align-self-center">
              <span data-icon-after=""></span>
            </div>
          </CKCollapsible.Button>
          <AnimatePresence mode="sync">
            {open ? (
              <motion.div
                transition={{
                  duration: 0.3,
                  ease: [0.55, 0.055, 0.675, 0.19],
                }}
                initial={{ height: 0 }}
                animate={{ height: "auto" }}
                exit={{ height: 0 }}
                key={name}
                className="overflow-hidden"
              >
                <CKFilter.Group className="mb-1.25 overflow-hidden">
                  <ul className="filters__filter-options px-3 px-lg-0">
                    {options &&
                      options.map((item, index) => (
                        <CKFilter.Item
                          value={item}
                          key={`${name}_${item.value}_${index}`}
                          className="mt-3 mt-lg-1"
                        >
                          {({ selected, onUpdate }) => (
                            <label className="input input--checkbox filters__option">
                              <div className="input__group d-flex no-gutters">
                                <input
                                  type="checkbox"
                                  name={item.value}
                                  className="input__field checked"
                                  onChange={(checked) => {
                                    onUpdate({
                                      type: "checkbox",
                                      origin: [item],
                                      checked: checked.target.checked,
                                    });
                                  }}
                                />
                                <div className="col-auto" data-icon-before="">
                                  {selected ? (
                                    <CheckBoxChecked />
                                  ) : (
                                    <CheckBoxUnCheckedMobile />
                                  )}
                                </div>
                                <div className="col d-flex align-items-center pl-2">
                                  <span className="input__checkbox-label text light">
                                    {formatFilterValue(item.value, $pt)}
                                    {item.count > 0 && (
                                      <span className="ml-1 text-muted">
                                        ({item.count})
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </div>
                            </label>
                          )}
                        </CKFilter.Item>
                      ))}
                  </ul>
                </CKFilter.Group>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </>
      )}
    </CKCollapsible>
  );
};

const WbFilterGroup = {
  Desktop,
  Mobile,
};
export default WbFilterGroup;
